'use memo';

import Icon from '@/components/Icon/Icon';
import { cn } from '@/lib/cn';
import { getShortcutDefinition, type ShortcutId } from '../registry/definitions';
import type { KeyCombo, ShortcutPlatform } from '../types';
import { keyComboParts } from '../utils/keyComboParts';
import { currentPlatform, formatKeyLabel } from '../utils/platform';
type ShortcutLabelProps = {
  /** registry 定義の id（推奨）。combo と排他 */
  shortcutId?: ShortcutId;
  /** registry 外の一時的な表示（KeyCombo 正規形） */
  combo?: KeyCombo;
  /**
   * 表示形式。'icon' = キーをアイコンで示す（既定）/ 'text' = 記号テキストで説明する。
   * 「アイコンでショートカットを示す」と「ラベルで説明する」の 2 ユースケースに対応。
   */
  variant?: 'icon' | 'text';
  /** 暗い背景上に置く場合の反転スタイル */
  tone?: 'light' | 'dark';
  /** platform 上書き（既定は実行環境。Storybook で両 platform を並べる用途） */
  platform?: ShortcutPlatform;
  className?: string;
};

/**
 * kbd スタイルのキーキャップ表示（platform 自動切替）。
 * icon variant: モディファイア / Return を lucide アイコン（Alt(win) はオリジナルアイコン）で描画。
 * text variant: ⌘K / Ctrl+K の記号テキストで描画。
 * LauncherFooter / PromptDock / InitiateTopbarButton から root 経由で利用される。
 */
export function ShortcutLabel({
  shortcutId,
  combo,
  variant = 'icon',
  tone = 'light',
  platform: platformOverride,
  className
}: ShortcutLabelProps) {
  const platform = platformOverride ?? currentPlatform();
  const resolvedCombo = shortcutId ? getShortcutDefinition(shortcutId).keys[platform] : combo;
  if (!resolvedCombo) return null;
  const parts = keyComboParts(resolvedCombo, platform);

  // text variant: 説明文向けに綴り字ラベルを連結する（"Command + Return"）。kbd ではなく素のテキスト
  if (variant === 'text') {
    return <span className={className}>
        {parts.map(part => part.label).join(' + ')}
      </span>;
  }
  return <kbd
  // icon variant では記号列テキストをアクセシブル名にし、内側のアイコン/文字は装飾扱いにする
  aria-label={formatKeyLabel(resolvedCombo, platform)} className={cn('inline-flex h-[19px] min-w-[19px] items-center justify-center gap-0.5 rounded-[5px] border px-[5px] font-semibold leading-none', 'text-[11px]', tone === 'light' ? 'border-neutral-border bg-neutral-fill text-neutral-on-muted-fill' : 'border-primary-muted-on-fill bg-primary-fill-hovered text-primary-on-fill', className)}>
      {parts.map((part, index) => part.iconName ? <Icon key={`${part.label}-${index}`} iconName={part.iconName}
    // 11px のキーキャップ文字に合わせた 13px。IconSizeType に 13 が無いため
    // className でサイズ指定する（icon.md 推奨の className 経由サイジング）
    className="size-[13px]" inheritColor /> : <span key={`${part.label}-${index}`} aria-hidden>
            {part.label}
          </span>)}
    </kbd>;
}